import { useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * HeroGrid
 *
 * A premium animated 3D perspective wireframe grid rendered with Three.js.
 * Designed to live ONLY behind the hero section content: it is absolutely
 * positioned, sits at z-0 beneath the hero content (z-10), has pointer events
 * disabled, and is clipped to its parent container.
 *
 * The grid is a subdivided plane rendered as a wireframe via a custom
 * ShaderMaterial. The vertex shader applies a subtle procedural wave to suggest
 * a living data landscape; the fragment shader fades the lines toward the
 * horizon and adds a soft teal glow.
 *
 * Performance:
 *  - Clamps device pixel ratio.
 *  - Reduces grid density on small viewports.
 *  - Pauses rendering when the hero scrolls out of view (IntersectionObserver).
 *  - Fully disposes GPU resources and listeners on unmount.
 *  - Respects prefers-reduced-motion (renders a single static frame, no
 *    wave motion and no parallax).
 *
 * @param {Object}  props
 * @param {string}  [props.gridColor='#00D1B2']     Teal/cyan line color.
 * @param {number}  [props.lineOpacity=0.32]        Base opacity of grid lines (0-1).
 * @param {number}  [props.waveAmplitude=0.55]      Height of the procedural wave displacement.
 * @param {number}  [props.waveSpeed=0.35]          Speed of the wave motion.
 * @param {number}  [props.perspectiveStrength=1]   Multiplier for camera tilt / perspective depth.
 * @param {number}  [props.glowIntensity=0.6]       Strength of the atmospheric glow near the lines (0-1+).
 * @param {number}  [props.parallaxStrength=0.6]    How strongly the cursor shifts the camera (0 disables).
 * @param {boolean} [props.prefersReducedMotion=false] When true, disables motion + parallax.
 * @param {string}  [props.className='']            Extra classes for the wrapper.
 */
const HeroGrid = ({
  gridColor = '#00D1B2',
  lineOpacity = 0.18,
  waveAmplitude = 0.55,
  waveSpeed = 0.35,
  perspectiveStrength = 1,
  glowIntensity = 0.35,
  parallaxStrength = 0.6,
  prefersReducedMotion = false,
  className = ''
}) => {
  const containerRef = useRef(null);
  // Hold the latest tunable values so the animation loop can read them without
  // tearing down the whole scene when a prop changes.
  const paramsRef = useRef({
    waveAmplitude,
    waveSpeed,
    glowIntensity,
    parallaxStrength,
    prefersReducedMotion
  });

  useEffect(() => {
    paramsRef.current = {
      waveAmplitude,
      waveSpeed,
      glowIntensity,
      parallaxStrength,
      prefersReducedMotion
    };
  }, [waveAmplitude, waveSpeed, glowIntensity, parallaxStrength, prefersReducedMotion]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof window === 'undefined') return undefined;

    // Bail out gracefully if WebGL is unavailable (older devices, headless, etc.).
    const probe = document.createElement('canvas');
    const hasWebGL = !!(
      probe.getContext('webgl2') ||
      probe.getContext('webgl') ||
      probe.getContext('experimental-webgl')
    );
    if (!hasWebGL) return undefined;

    const color = new THREE.Color(gridColor);

    // ---- Renderer ---------------------------------------------------------
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true, // transparent so the hero gradient/glows show through
      powerPreference: 'high-performance'
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    container.appendChild(renderer.domElement);
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';

    // ---- Scene & Camera ---------------------------------------------------
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, 1, 0.1, 100);
    const baseCameraPos = new THREE.Vector3(0, 2.4, 7);
    camera.position.copy(baseCameraPos);
    camera.lookAt(0, -0.5 * perspectiveStrength, -6);

    // ---- Geometry ---------------------------------------------------------
    // Lower density on small screens to keep the GPU/CPU cost down.
    const isSmall = window.innerWidth < 768;
    const segments = isSmall ? 40 : 80;
    const planeWidth = 60;
    const planeDepth = 60;
    const geometry = new THREE.PlaneGeometry(planeWidth, planeDepth, segments, segments);
    // Lay the plane flat (it defaults to facing the camera in the XY plane).
    geometry.rotateX(-Math.PI / 2);

    // ---- Shader material (wireframe grid + wave + horizon fade + glow) -----
    const uniforms = {
      uTime: { value: 0 },
      uColor: { value: color },
      uLineOpacity: { value: lineOpacity },
      uWaveAmplitude: { value: prefersReducedMotion ? waveAmplitude * 0.35 : waveAmplitude },
      uWaveSpeed: { value: waveSpeed },
      uGlow: { value: glowIntensity },
      uFadeNear: { value: 4.0 },
      uFadeFar: { value: 34.0 }
    };

    const material = new THREE.ShaderMaterial({
      uniforms,
      transparent: true,
      depthWrite: false,
      wireframe: true,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        uniform float uTime;
        uniform float uWaveAmplitude;
        uniform float uWaveSpeed;
        varying float vDist;

        void main() {
          vec3 pos = position;

          // Procedural wave: combine a couple of sine bands across X and Z so
          // the surface gently undulates like a living data landscape.
          float t = uTime * uWaveSpeed;
          float wave =
              sin(pos.x * 0.25 + t) * 0.6
            + sin(pos.z * 0.32 - t * 0.8) * 0.5
            + sin((pos.x + pos.z) * 0.15 + t * 0.5) * 0.4;

          // Dampen the wave near the camera (front edge) so content sits over a
          // calmer area, and let it grow toward the horizon.
          float depthFactor = smoothstep(2.0, 28.0, -pos.z + 30.0);
          pos.y += wave * uWaveAmplitude * (0.4 + depthFactor);

          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          // Distance from camera in view space, used for horizon fade.
          vDist = -mvPosition.z;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: /* glsl */ `
        precision mediump float;
        uniform vec3 uColor;
        uniform float uLineOpacity;
        uniform float uGlow;
        uniform float uFadeNear;
        uniform float uFadeFar;
        varying float vDist;

        void main() {
          // Fade lines out as they recede toward the horizon.
          float fade = 1.0 - smoothstep(uFadeNear, uFadeFar, vDist);

          // Soft glow boost on the closer lines for atmospheric depth.
          float glow = uGlow * fade;
          vec3 col = uColor * (1.0 + glow);

          float alpha = uLineOpacity * fade;
          if (alpha < 0.003) discard;

          gl_FragColor = vec4(col, alpha);
        }
      `
    });

    const mesh = new THREE.Mesh(geometry, material);
    // Push the grid down and back so it reads as ground beneath the content,
    // occupying the lower portion of the hero.
    mesh.position.set(0, -2.2, -8);
    scene.add(mesh);

    // ---- Resize handling --------------------------------------------------
    const setSize = () => {
      const w = container.clientWidth || window.innerWidth;
      const h = container.clientHeight || window.innerHeight;
      if (w === 0 || h === 0) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    setSize();

    const resizeObserver = new ResizeObserver(setSize);
    resizeObserver.observe(container);

    // ---- Mouse parallax ---------------------------------------------------
    const target = { x: 0, y: 0 }; // normalized cursor (-1..1)
    const current = { x: 0, y: 0 }; // damped value
    const handlePointerMove = (event) => {
      const rect = container.getBoundingClientRect();
      const nx = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = ((event.clientY - rect.top) / rect.height) * 2 - 1;
      target.x = Math.max(-1, Math.min(1, nx));
      target.y = Math.max(-1, Math.min(1, ny));
    };
    const handlePointerLeave = () => {
      target.x = 0;
      target.y = 0;
    };
    // Listen on the hero (parent) since the canvas has pointer-events: none.
    const interactionEl = container.parentElement || container;
    interactionEl.addEventListener('pointermove', handlePointerMove, { passive: true });
    interactionEl.addEventListener('pointerleave', handlePointerLeave, { passive: true });

    // ---- Visibility gating (pause when hero is off-screen) ----------------
    let isVisible = true;
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        isVisible = entries[0]?.isIntersecting ?? true;
      },
      { threshold: 0 }
    );
    intersectionObserver.observe(container);

    // ---- Render loop ------------------------------------------------------
    const clock = new THREE.Clock();
    let rafId = 0;

    const renderFrame = () => {
      const { parallaxStrength: pStrength, prefersReducedMotion: reduced } = paramsRef.current;

      // Sync live-tunable uniforms.
      uniforms.uWaveAmplitude.value = reduced
        ? paramsRef.current.waveAmplitude * 0.35
        : paramsRef.current.waveAmplitude;
      uniforms.uWaveSpeed.value = paramsRef.current.waveSpeed;
      uniforms.uGlow.value = paramsRef.current.glowIntensity;

      if (!reduced) {
        uniforms.uTime.value = clock.getElapsedTime();

        // Damped parallax: lerp the camera offset toward the cursor target.
        current.x += (target.x - current.x) * 0.05;
        current.y += (target.y - current.y) * 0.05;
        const amp = 0.9 * pStrength;
        camera.position.x = baseCameraPos.x + current.x * amp;
        camera.position.y = baseCameraPos.y - current.y * amp * 0.5;
        camera.lookAt(0, -0.5 * perspectiveStrength, -6);
      }

      renderer.render(scene, camera);
    };

    const animate = () => {
      rafId = requestAnimationFrame(animate);
      if (!isVisible) return;
      renderFrame();
    };

    if (prefersReducedMotion) {
      // Render a single static frame; no animation loop.
      renderFrame();
    } else {
      animate();
    }

    // ---- Cleanup ----------------------------------------------------------
    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      interactionEl.removeEventListener('pointermove', handlePointerMove);
      interactionEl.removeEventListener('pointerleave', handlePointerLeave);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
    // Recreate the scene only on identity/structural prop changes; smooth
    // per-frame params (wave/glow/parallax) are read live via paramsRef.
  }, [gridColor, lineOpacity, perspectiveStrength, prefersReducedMotion]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className={`absolute inset-0 z-0 overflow-hidden pointer-events-none ${className}`}
    />
  );
};

export default HeroGrid;
